import React from 'react';
import _ from 'lodash';
import {GridColumn, GridToolbar} from '@progress/kendo-react-grid';
import {PropTypes} from 'prop-types';
import {BaasicTableActions, LanguageMetadata, FormatterResolver, BaasicButton} from 'core/components';
import {defaultTemplate} from 'core/hoc';
import {isSome} from 'core/utils';

function hasAction(actions) {
    return actions && (actions.onEdit || actions.onDelete || actions.onPreview);
}

const LocalizedCell = defaultTemplate(({ onClick, className, dataItem, field, icon, format }) => {
    return (
        <td className={className} {...(onClick ? { onClick: () => onClick(dataItem, field) } : {})}>
            {icon && <i className={"icomoon icon-" + dataItem[icon] + " spc--right--sml align--v--sub"}/>}

            {
                _.isNil(format)
                    ? <LanguageMetadata item={dataItem} propertyName={field} />
                    : <FormatterResolver item={dataItem} field={field} format={format} />
            } 
        </td>
    )
});

function defaultRenderColumnsTemplate({ t, columns }) {
    return columns.map(({ key = null, title = null, cell = null, headerCell = null, icon = null, onClick = null, innerColumns = null, format, className, sortable = true, ...otherProps }, idx) => {
            const DataCell = cell ? cell : (props) => <LocalizedCell onClick={onClick} icon={icon} {...props} format={format}/>

            // Kendo Grid v14 moved cell/headerCell to a `cells` settings object.
            // Sortable columns get the default Kendo header (clickable sort link);
            // non-sortable columns render their own th with our static label.
            const cellsSettings = innerColumns ? {} : { data: DataCell };
            if (headerCell) {
                // Column configs supply header *content* (v3 contract, where Kendo wrapped
                // it in the th); under v14 cells.headerCell must render the th itself.
                const HeaderContent = headerCell;
                cellsSettings.headerCell = ({ thProps }) => (
                    <th {...thProps}><HeaderContent /></th>
                );
            } else if (!sortable) {
                cellsSettings.headerCell = ({ thProps }) => (
                    <th {...thProps}>
                        <span className="k-link u-cursor--default">{t(title)}</span>
                    </th>
                );
            }

            return (
                    <GridColumn
                    className={`${className ? className : ""} ${onClick ? "c-pointer table--clickable" : ""}`}
                    key={key || idx}
                    field={key}
                    title={t(title)}
                    cells={cellsSettings}
                    {...otherProps}
                    >
                    {innerColumns ? defaultRenderColumnsTemplate({ t, columns: innerColumns }) : null}
                    </GridColumn>
            );

        }
    );
}

function defaultRenderSelectColumn() {
    return (
            <GridColumn
            field={"selected"}
            width="50px"/>                
    );
}

function defaultRenderActionsTemplate({ actions, actionsComponent, authorization, t, actionHeaderComponent}) {
    const { actionsWidth } = actions;
    const actionCount = (actions ? actions.visible : null) || _.size(actions);

    if (!hasAction(actions) && !actionsComponent) return null;

    const ActionsCell = (cellProps) => (
        <BaasicTableActions {...cellProps} actions={actions} authorization={authorization} actionsComponent={actionsComponent} t={t} actionHeaderComponent={actionHeaderComponent} />
    );

    const HeaderCell = ({ thProps }) => (
        <th {...thProps}>
            {actionHeaderComponent
                ? React.createElement(actionHeaderComponent, { t: t })
                : <a className="k-link type--right u-cursor--default">{t('GRID.ACTIONS_COLUMN')}</a>}
        </th>
    );

    return (
        <GridColumn
            groupable={false}
            sortable={false}
            filterable={false}
            resizable={false}
            width={actionsWidth ? actionsWidth : actionCount < 3 ? 80 : 30 * actionCount}
            cells={{ data: ActionsCell, headerCell: HeaderCell }}
        />
    );
}
function defaultRenderBatchActionsToolbarTemplate(tableStore, authorization) {
    const { isBatchSelect, config: { batchActions }, hasSelectedItems, hasDirtyItems, selectedItems, resetGridItems, data } = tableStore;
    const { onBatchDelete, onBatchUpdate } = batchActions;
    if (!isSome(onBatchDelete) && !isSome(onBatchUpdate)) {
        return null;
    }

    return (
        <GridToolbar>
            <div className="sticky--left">
                {isSome(onBatchUpdate) && authorization.update &&(
                    <React.Fragment>
                        <BaasicButton 
                            authorization={authorization ? authorization.update : null}
                            className="icon-floppy-disk btn btn--icon spc--right--tny" 
                            disabled={!hasDirtyItems}
                            icon='update'
                            onlyIcon={true}
                            onClick={() => onBatchUpdate(data.toJS())}>
                        </BaasicButton>
                        {hasDirtyItems && (
                            <BaasicButton 
                                className="btn btn--icon spc--right--tny" 
                                onlyIcon={true}
                                icon='clear'
                                onClick={() => resetGridItems()}>
                            </BaasicButton>
                        )}
                    </React.Fragment>
                )}
                {isSome(onBatchDelete) && isBatchSelect && (
                    <BaasicButton                     
                        authorization={authorization ? authorization.delete : null}
                        className="btn btn--icon btn--icon--primary icon-bin align--v--middle" 
                        disabled={!hasSelectedItems}
                        icon='delete'
                        onlyIcon={true}
                        label='delete'
                        onClick={() => onBatchDelete(selectedItems.toJS())}>
                    </BaasicButton>
                )}
            </div>
        </GridToolbar>
    );
}

function defaultRenderNoRecordsTemplate(noRecordsComponent) {
    if (!noRecordsComponent) return 'No records available';

    return noRecordsComponent;
}

defaultRenderActionsTemplate.propTypes = {
    actions: PropTypes.object,
    actionsComponent: PropTypes.array,
    actionsWidth: PropTypes.any,
    actionHeaderComponent: PropTypes.any,
    authorization: PropTypes.any,
    t: PropTypes.func
}

defaultRenderBatchActionsToolbarTemplate.propTypes = {
    tableStore: PropTypes.object,
    authorization: PropTypes.any,
    t: PropTypes.func
}

defaultRenderSelectColumn.propTypes = {
}

export {
    defaultRenderActionsTemplate,
    defaultRenderColumnsTemplate,
    defaultRenderBatchActionsToolbarTemplate,
    defaultRenderNoRecordsTemplate,
    defaultRenderSelectColumn
}